import React, {memo, useState} from 'react'
import {OmicsFormattedTextBlock} from '../ai-chat/omicsFormattedTextBlock'
import {OmicsMarkdownBody} from '../ai-chat/omicsAssistantMarkdown'
import {OmicsToolCollapsibleRow} from '../ai-chat/omicsToolCollapsibleRow'
import {formatToolDisplayLabel} from '../ai-chat/toolDisplayLabels'
import {formatIncompleteErrorText, formatToolResultMeta} from '../ai-chat/workingSummary'

export type CodeExecToolStatus =
  | {type: 'running'}
  | {type: 'complete'}
  | {type: 'incomplete'; error?: unknown}
  | {type: 'requires-action'; reason?: string}

/** @deprecated Use {@link CodeExecToolStatus}. */
export type PythonToolStatus = CodeExecToolStatus

function languageSubtitle(summarySuffix?: string): string | undefined {
  const trimmed = summarySuffix?.replace(/^\s*·\s*/, '').trim()
  return trimmed || undefined
}

/** Code-exec tools: CODE (+ optional OTHER) and RESULT — no Parameters JSON duplication. */
export const CodeExecToolCollapsible: React.FC<{
  toolName: string
  code: string
  fenceLanguage: string
  other: Record<string, unknown>
  resultText?: string
  status: CodeExecToolStatus
  isError?: boolean
  summarySuffix?: string
}> = memo(({toolName, code, fenceLanguage, other, resultText = '', status, isError, summarySuffix}) => {
  const [open, setOpen] = useState(false)
  const label = formatToolDisplayLabel(toolName)
  const pending = status.type === 'running' || status.type === 'requires-action'
  const failed = Boolean(isError) || status.type === 'incomplete'
  const errText = formatIncompleteErrorText(status)
  const otherKeys = Object.keys(other)
  const codeTrimmed = code.trim()
  const resultTrimmed = (resultText ?? '').trim()
  const lang = (fenceLanguage || 'text').toLowerCase()

  return (
    <OmicsToolCollapsibleRow
      className='omics-aui-tool omics-aui-tool-keyed omics-aui-tool-python'
      title={label}
      subtitle={languageSubtitle(summarySuffix)}
      meta={formatToolResultMeta({pending, failed, errorText: errText, resultText: resultTrimmed})}
      pending={pending}
      failed={failed}
      open={open}
      onToggle={() => setOpen((o) => !o)}
    >
      {codeTrimmed ? (
        <>
          <div className='ai-chat-tool-section-label'>Code</div>
          <OmicsMarkdownBody markdown={`\`\`\`${lang}\n${codeTrimmed}\n\`\`\``} />
        </>
      ) : null}
      {otherKeys.length > 0 ? (
        <>
          <div className='ai-chat-tool-section-label ai-chat-tool-section-sub'>Other</div>
          <OmicsMarkdownBody markdown={`\`\`\`json\n${JSON.stringify(other, null, 2)}\n\`\`\``} />
        </>
      ) : null}
      {!pending && resultTrimmed ? (
        <>
          <div className='ai-chat-tool-section-label'>Result</div>
          <OmicsFormattedTextBlock text={resultTrimmed} className='ai-chat-tool-result-output' />
        </>
      ) : null}
      {failed && errText ? (
        <div role='alert' className='ai-chat-tool-error mt-2'>
          {errText}
        </div>
      ) : null}
    </OmicsToolCollapsibleRow>
  )
})

CodeExecToolCollapsible.displayName = 'CodeExecToolCollapsible'

/** @deprecated Use {@link CodeExecToolCollapsible}. */
export const PythonToolCollapsible: React.FC<{
  toolName: string
  code: string
  other: Record<string, unknown>
  resultText?: string
  status: CodeExecToolStatus
  isError?: boolean
}> = memo((props) => (
  <CodeExecToolCollapsible {...props} fenceLanguage='python' />
))

PythonToolCollapsible.displayName = 'PythonToolCollapsible'
